import { useEffect, useRef, useState } from 'react';
import { sendCoach, sendCoachStream, applyOperations, saveCoachLog, fetchAree, invalidateCache } from './api';
import { openWithSummaryStream } from './coachOpener';
import { formatAppliedResult } from './coachSummary';
import { flashMovedIds, flashCoachTouched } from './movedFlash';
import { toast } from 'sonner';

/**
 * Hook condiviso fra SetupChat / QuarterlyPlanChat / MonthlyPlanChat / WeeklyReviewChat.
 *
 * Gestisce lo stato e i flussi comuni:
 *  - caricamento stato (con resume da sessionStorage o AI Opener)
 *  - invio messaggi (con supporto override per chip rapidi)
 *  - conferma / cancel / edit delle operazioni proposte dal coach
 *  - scroll automatico e focus sulla textarea
 *
 * Per-chat differences come `momento`, fetcher dello stato, label di ripresa
 * e parametro periodico sono passati come prop.
 */
export function useCoachChat({
  momento,
  fetchStateFn,
  param,
  resumeGreetingText,
  transformOpsBeforeApply,
  onAfterConfirm,
}) {
  const [stato, setStato] = useState(null);
  const [aree, setAree] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pendingOps, setPendingOps] = useState(null);
  const [editingOpsJson, setEditingOpsJson] = useState(null);
  const [highlightIds, setHighlightIds] = useState([]);
  // Contatore auto-continue consecutivi (feature 2). Cap a 3 per evitare loop
  // se il coach continua a proporre operazioni turno dopo turno.
  const autoContinueRef = useRef(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // True dopo che `saveCoachLog` è stato chiamato (applicato/annullato/chat).
  // Evita doppi-save al cleanup quando l'utente ha già confermato/annullato.
  const savedRef = useRef(false);
  // Riferimento sempre aggiornato all'array messages corrente, per il save di unmount.
  // Evita closure stale (cleanup useEffect non vede l'ultimo `messages`).
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const resumeKey = `qp.coach.resume.${momento}`;
  // Session key — per persistenza cross-navigation (feature 1).
  // Distinta da resumeKey (usato solo per "Riprendi" dallo Storico Coach).
  // sessionStorage sopravvive alla navigazione fra pagine ma non alla chiusura tab.
  const sessionKey = `qp.coach.session.${momento}.${param || 'default'}`;

  const loadState = async (target = param) => {
    const s = await fetchStateFn(target);
    setStato(s);
    return s;
  };

  // Carica le aree dell'utente una volta sola (con i colori) per evidenziarle
  // nei messaggi del coach. Best-effort: se fallisce, niente colori.
  useEffect(() => {
    let cancelled = false;
    fetchAree()
      .then((r) => { if (!cancelled) setAree(r?.items || []); })
      .catch(() => { /* no-op */ });
    return () => { cancelled = true; };
  }, []);

  // Mount: carica stato + (resume oppure AI opener).
  useEffect(() => {
    savedRef.current = false; // nuova sessione, riabilita autosave al cleanup
    loadState(param).then(async (s) => {
      // 1. Prima priorità: sessione salvata cross-navigation (feature 1).
      //    Se l'utente aveva già iniziato una chat per questo momento+param,
      //    la ripristina intatta senza dover ricominciare da capo.
      try {
        const rawSession = sessionStorage.getItem(sessionKey);
        if (rawSession) {
          const parsed = JSON.parse(rawSession);
          if (Array.isArray(parsed.messaggi) && parsed.messaggi.length > 0) {
            setMessages(parsed.messaggi);
            if (parsed.pendingOps) setPendingOps(parsed.pendingOps);
            return;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('[useCoachChat] session restore failed:', err);
      }

      let resumed = null;
      try {
        const raw = sessionStorage.getItem(resumeKey);
        if (raw) {
          resumed = JSON.parse(raw);
          sessionStorage.removeItem(resumeKey);
        }
      } catch (err) {
        // sessionStorage può contenere JSON corrotto da una build precedente:
        // lo trattiamo come "niente da riprendere" e procediamo con greeting normale.
        if (process.env.NODE_ENV !== 'production') console.debug('[useCoachChat] resume parse failed:', err);
      }

      if (resumed && Array.isArray(resumed.messaggi) && resumed.messaggi.length > 0) {
        const divider = {
          role: 'assistant',
          content: `— Conversazione precedente caricata dallo Storico Coach. Continua da qui. —`,
          isDivider: true,
        };
        const greeting = {
          role: 'assistant',
          content: typeof resumeGreetingText === 'function'
            ? resumeGreetingText(s)
            : (resumeGreetingText || 'Riprendiamo da dove avevamo lasciato.'),
        };
        setMessages([...resumed.messaggi, divider, greeting]);
        return;
      }

      setSending(true);
      // Inserisci subito il trigger + un assistant vuoto in streaming.
      const trigger = { role: 'user', content: '', hidden: true };
      const initialAssistant = { role: 'assistant', content: '', streaming: true };
      setMessages([trigger, initialAssistant]);
      const result = await openWithSummaryStream(momento, s, {
        onDelta: (delta) => {
          setMessages((prev) => prev.map((m, i) =>
            i === 1 ? { ...m, content: (m.content || '') + delta } : m,
          ));
        },
      });
      // Finalizza con i contenuti definitivi (incluse eventuali ops).
      setMessages([
        result.trigger,
        {
          ...result.assistant,
          streaming: false,
        },
      ]);
      // openWithSummary potrebbe aver ritornato operazioni — registra pendingOps.
      if (result.assistant?.operazioni && result.assistant.operazioni.length > 0) {
        setPendingOps({ operazioni: result.assistant.operazioni, msgIdx: 1 });
      }
      setSending(false);
    });
    // Mount-only effect: deve girare quando `param` cambia (es. navigazione
    // settimana → settimana). loadState/resumeGreetingText/momento/resumeKey
    // sono volutamente esclusi: catturare la closure stale al mount è desiderato
    // (l'AI opener parte UNA volta per `param` e poi le interazioni passano da
    // handleSend, che non dipende da queste closure).
  }, [param]);

  // Scroll auto al fondo (la chat è reverse-chrono, quindi 'fondo' = sopra).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Persist chat cross-navigation (feature 1). Ogni cambio di `messages` o
  // `pendingOps` viene serializzato in sessionStorage. Al mount, se esiste
  // una sessione, viene ripristinata. La sessione viene rimossa solo quando
  // l'utente chiama `endSession()` (pulsante "Fine pianificazione").
  useEffect(() => {
    // Non salvare se non c'è ancora nessun messaggio significativo (evita
    // clobber della sessione precedente durante la fase iniziale di mount).
    const visible = (messages || []).filter((m) => !m.hidden && !m.isDivider);
    if (visible.length === 0) return;
    try {
      sessionStorage.setItem(
        sessionKey,
        JSON.stringify({ messaggi: messages, pendingOps, ts: Date.now() }),
      );
    } catch (err) {
      // sessionStorage può essere pieno (quota exceeded) o SSR: ignora.
      if (process.env.NODE_ENV !== 'production') console.debug('[useCoachChat] session save failed:', err);
    }
  }, [messages, pendingOps, sessionKey]);

  /**
   * Chiude la sessione corrente: salva il log finale ("applicato" se ci sono
   * state ops, altrimenti "chat"), pulisce sessionStorage, resetta i messages.
   * Chiamato dal pulsante "Fine pianificazione/review" o programmaticamente.
   */
  const endSession = () => {
    try { sessionStorage.removeItem(sessionKey); } catch { /* noop */ }
    const msgs = messagesRef.current || [];
    const visible = msgs.filter((m) => !m.hidden && !m.isDivider);
    if (visible.length >= 2 && !savedRef.current) {
      saveCoachLog({ momento, messaggi: visible, operazioni: [], esito: 'chat' }).catch(() => {});
      savedRef.current = true;
    }
    setMessages([]);
    setPendingOps(null);
    setEditingOpsJson(null);
    toast.success('Sessione chiusa · storico salvato');
  };

  // Autosave al cleanup: se l'utente esce dalla chat senza confermare/annullare
  // e c'è stato uno scambio significativo (almeno 1 user + 1 assistant non vuoto),
  // salva la conversazione come `esito: 'chat'` per la memoria del coach.
  useEffect(() => {
    return () => {
      if (savedRef.current) return;
      const msgs = messagesRef.current || [];
      // Filtra trigger nascosti e divider
      const visible = msgs.filter((m) => !m.hidden && !m.isDivider);
      const hasUser = visible.some((m) => m.role === 'user' && (m.content || '').trim());
      const hasAssistant = visible.some((m) => m.role === 'assistant' && (m.content || '').trim());
      if (!hasUser || !hasAssistant) return;
      // Fire-and-forget: window può chiudersi, ma il browser invia comunque la POST.
      saveCoachLog({ momento, messaggi: visible, operazioni: [], esito: 'chat' }).catch(() => {});
      savedRef.current = true;
    };
    // Cleanup-only effect: i ref `savedRef`/`messagesRef` non vanno in deps
    // (sono mutable ref con valore sempre aggiornato per la closure).
  }, [momento]);

  // Focus alla textarea quando il coach finisce di rispondere.
  useEffect(() => {
    if (!sending) inputRef.current?.focus();
  }, [sending]);

  const handleSend = async (textOverride, opts = {}) => {
    const hasOverride = typeof textOverride === 'string';
    const text = (hasOverride ? textOverride : input).trim();
    if (!text || sending) return;
    // Reset counter auto-continue quando l'utente invia MANUALMENTE (input in UI).
    // Questo permette nuovi cicli di auto-continue dopo interazione umana.
    if (!opts.hidden) {
      autoContinueRef.current = 0;
    }
    setHighlightIds([]);
    if (!hasOverride) setInput('');
    // Il messaggio user è "hidden" (auto-continue) o normale.
    const userMsg = opts.hidden
      ? { role: 'user', content: text, hidden: true }
      : { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    // Inserisci subito un messaggio assistant VUOTO che verrà popolato a delta.
    const placeholderIdx = newMessages.length;
    const optimistic = [...newMessages, { role: 'assistant', content: '', streaming: true }];
    setMessages(optimistic);
    setSending(true);

    const payload = newMessages
      .filter((_, idx) => !(idx === 0 && newMessages[0].role === 'assistant'))
      .map(({ role, content }) => ({ role, content }));
    const reqArgs = {
      momento,
      stato,
      messaggi: payload.length ? payload : [{ role: 'user', content: text }],
    };

    try {
      // Streaming via SSE: aggiorna il messaggio assistant in tempo reale.
      await sendCoachStream({
        ...reqArgs,
        onDelta: (delta) => {
          setMessages((prev) => prev.map((m, i) =>
            i === placeholderIdx ? { ...m, content: (m.content || '') + delta } : m,
          ));
        },
        onDone: ({ messaggio, operazioni, suggerimenti }) => {
          setMessages((prev) => prev.map((m, i) =>
            i === placeholderIdx
              ? {
                ...m,
                content: messaggio || m.content || '…',
                operazioni: operazioni && operazioni.length > 0 ? operazioni : null,
                suggerimenti: Array.isArray(suggerimenti) ? suggerimenti : [],
                streaming: false,
              }
              : m,
          ));
          if (operazioni && operazioni.length > 0) {
            setPendingOps({ operazioni, msgIdx: placeholderIdx });
          }
        },
      });
    } catch (streamErr) {
      // Fallback non-streaming: la chat resta sempre usabile.
      console.warn('[coach-stream] fallback:', streamErr?.message);
      try {
        const res = await sendCoach(reqArgs);
        setMessages((prev) => prev.map((m, i) =>
          i === placeholderIdx
            ? {
              ...m,
              content: res.messaggio || '…',
              operazioni: res.operazioni && res.operazioni.length > 0 ? res.operazioni : null,
              suggerimenti: Array.isArray(res.suggerimenti) ? res.suggerimenti : [],
              streaming: false,
            }
            : m,
        ));
        if (res.operazioni && res.operazioni.length > 0) {
          setPendingOps({ operazioni: res.operazioni, msgIdx: placeholderIdx });
        }
      } catch (e) {
        console.error(e);
        toast.error('Errore dal coach.');
        setMessages((prev) => prev.map((m, i) =>
          i === placeholderIdx ? { ...m, content: '⚠️ Errore nella chiamata al coach.', streaming: false } : m,
        ));
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConfirmOps = async () => {
    if (!pendingOps) return;
    setApplying(true);
    try {
      let ops = pendingOps.operazioni;
      if (editingOpsJson) {
        try { ops = JSON.parse(editingOpsJson); }
        catch { toast.error('JSON non valido'); setApplying(false); return; }
      }
      if (typeof transformOpsBeforeApply === 'function') {
        ops = transformOpsBeforeApply(ops);
      }
      const resp = await applyOperations(ops);
      invalidateCache('stats:closed-week');
      // Conta i duplicati bloccati server-side (bug coach: crea su elementi già esistenti).
      // Il backend, quando riconosce un duplicato, restituisce il record esistente
      // con flag `duplicato_evitato: true` invece di inserire una seconda riga.
      const nDup = (resp?.results || []).filter((r) => r?.duplicato_evitato).length;
      if (nDup > 0) {
        toast.info(
          nDup === 1
            ? '1 task era già presente · riutilizzata invece di duplicarla'
            : `${nDup} task erano già presenti · riutilizzate invece di duplicarle`,
          { duration: 5000 },
        );
      }
      // Toast dedicato per gli spostamenti (aggiorna azione/mit con data/settimana).
      // Rende evidente all'utente che il coach ha spostato l'elemento invece di
      // duplicarlo (cross-check del fix Bug 2 lato UI).
      const moves = (resp?.results || []).filter((r) => (
        r?.azione === 'aggiorna'
        && (r?.entita === 'azione' || r?.entita === 'mit')
        && r?.data
        && (
          // il payload ops originale conteneva un cambio data/settimana
          (ops || []).some((o) => (
            o.azione === 'aggiorna'
            && o.entita === r.entita
            && o.id === r.data?.id
            && (o.campi?.data !== undefined || o.campi?.settimana !== undefined)
          ))
        )
      ));
      if (moves.length === 1) {
        const m = moves[0];
        const tipo = m.entita === 'mit' ? 'MIT' : 'Azione';
        const dest = m.data?.data || m.data?.settimana || '';
        const destFmt = /^\d{4}-\d{2}-\d{2}$/.test(dest)
          ? dest.slice(8, 10) + '/' + dest.slice(5, 7) + '/' + dest.slice(0, 4)
          : dest;
        toast.success(`${tipo} spostata al ${destFmt}`, { duration: 4000 });
      } else if (moves.length > 1) {
        toast.success(`${moves.length} task spostate`, { duration: 4000 });
      }
      // Flash badge ↻ sulle righe spostate per 3s (feature UX).
      // Gli id vengono raccolti dai `results` filtrati come `moves` sopra.
      flashMovedIds(moves.map((m) => m?.data?.id).filter(Boolean));
      toast.success('Stato aggiornato');
      const summary = (resp?.results || []).map(formatAppliedResult).filter(Boolean);
      const touched = (resp?.results || []).map((r) => r?.data?.id).filter(Boolean);
      saveCoachLog({ momento, messaggi: messages, operazioni: ops, esito: 'applicato' }).catch(() => {});
      savedRef.current = true;
      setPendingOps(null);
      setEditingOpsJson(null);
      setMessages((prev) => prev.map((m, i) =>
        i === pendingOps.msgIdx ? { ...m, operazioni: null, applied: true, appliedSummary: summary } : m,
      ));
      await loadState();
      setHighlightIds(touched);
      // Flash forte 10s sui KR/Azioni/MIT toccati (create/update/close) — usato
      // dal CoachStatePanel per far vedere in real-time cosa cambia durante
      // il dialogo. Include TUTTI gli id (superset di `moves`), non solo gli
      // spostamenti.
      flashCoachTouched(touched);
      if (typeof onAfterConfirm === 'function') {
        onAfterConfirm();
      }
      // Reset flag saved perché la sessione continua (non è stata chiusa
      // esplicitamente via endSession). Così se l'utente esce ora, l'effetto
      // di cleanup ri-salva il log aggiornato.
      savedRef.current = false;
      // AUTO-CONTINUE (feature 2): dopo aver applicato con successo, il coach
      // a volte si ferma e attende un "continuiamo". Lo invia automaticamente
      // in background (piccolo delay per lasciare respirare la UI), fino a
      // MAX 3 auto-continue consecutivi per evitare loop.
      const AUTO_CONTINUE_MAX = 3;
      if (autoContinueRef.current < AUTO_CONTINUE_MAX) {
        autoContinueRef.current += 1;
        setTimeout(() => {
          // Se nel frattempo l'utente ha già scritto qualcosa o è arrivato un
          // nuovo pendingOps, non forziamo l'auto-continue.
          if (sending || pendingOps || input?.trim()) return;
          handleSend('Continuiamo con il prossimo punto della pianificazione.', { hidden: true });
        }, 800);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Errore applicazione operazioni');
    } finally {
      setApplying(false);
    }
  };

  const handleCancelOps = () => {
    if (pendingOps) {
      saveCoachLog({ momento, messaggi: messages, operazioni: pendingOps.operazioni, esito: 'annullato' }).catch(() => {});
      savedRef.current = true;
    }
    setPendingOps(null);
    setEditingOpsJson(null);
    setMessages((prev) => prev.map((m, i) =>
      pendingOps && i === pendingOps.msgIdx ? { ...m, operazioni: null, cancelled: true } : m,
    ));
  };

  /**
   * Chiusura rapida di un item (obiettivo/key_result/azione/mit) direttamente
   * dal CoachStatePanel — bypassando la chat. Conferma + applyOperations + toast
   * con bottone "Annulla" che riapre l'item entro 5 secondi (undo).
   */
  const handleCloseItem = async (entita, id, item) => {
    const label = item?.titolo || item?.testo || id;
    const verbo = (entita === 'azione' || entita === 'mit') ? 'segnare come fatto' : 'chiudere';
    if (!window.confirm(`Vuoi ${verbo} «${label}»?`)) return;
    // Snapshot pre-close per l'undo (i campi rilevanti dipendono dall'entità).
    const snapshot = {};
    if (entita === 'obiettivo' || entita === 'key_result') snapshot.stato = item?.stato ?? 'attivo';
    if (entita === 'azione' || entita === 'mit') snapshot.fatto = item?.fatto ?? false;
    if (entita === 'review') snapshot.chiuso = item?.chiuso ?? false;
    try {
      await applyOperations([{ entita, azione: 'chiudi', id, campi: {} }]);
      invalidateCache('stats:closed-week');
      await loadState();
      setHighlightIds([id]);
      const fatto = entita === 'azione' || entita === 'mit';
      toast.success(fatto ? 'Segnato come fatto' : 'Chiuso', {
        duration: 5000,
        action: {
          label: 'Annulla',
          onClick: async () => {
            try {
              await applyOperations([{ entita, azione: 'aggiorna', id, campi: snapshot }]);
              invalidateCache('stats:closed-week');
              await loadState();
              setHighlightIds([id]);
              toast.success('Riaperto');
            } catch (err) {
              console.error(err);
              toast.error(err?.response?.data?.detail || 'Errore durante l\'annullamento');
            }
          },
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Errore durante la chiusura');
    }
  };

  // ── Riassegnazione data MIT / settimana Azione direttamente dalla sidebar
  // o dal calendario (popover + drag-and-drop). Pattern simmetrico a
  // handleCloseItem: applyOperations + invalidate cache + reload state +
  // highlight + toast con Undo.
  const handleUpdateItem = async (entita, id, campi, item) => {
    if (!campi || Object.keys(campi).length === 0) return;
    // Snapshot per undo: prendi gli stessi campi dal valore precedente.
    const snapshot = {};
    for (const k of Object.keys(campi)) {
      snapshot[k] = item?.[k];
    }
    try {
      await applyOperations([{ entita, azione: 'aggiorna', id, campi }]);
      invalidateCache('stats:closed-week');
      await loadState();
      setHighlightIds([id]);
      // Etichetta human-readable del cambio (es. "spostato a W27")
      const label =
        'data' in campi ? `spostato al ${campi.data}` :
        'settimana' in campi ? `spostato a ${campi.settimana.slice(-3)}` :
        'aggiornato';
      toast.success(label, {
        duration: 5000,
        action: snapshot && Object.values(snapshot).some((v) => v !== undefined && v !== null) ? {
          label: 'Annulla',
          onClick: async () => {
            try {
              await applyOperations([{ entita, azione: 'aggiorna', id, campi: snapshot }]);
              invalidateCache('stats:closed-week');
              await loadState();
              setHighlightIds([id]);
              toast.success('Ripristinato');
            } catch (err) {
              console.error(err);
              toast.error(err?.response?.data?.detail || 'Errore durante l\'annullamento');
            }
          },
        } : undefined,
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Errore durante lo spostamento');
      throw e; // ri-propaga così il popover gestisce pending=false
    }
  };

  return {
    // Stato
    stato,
    aree,
    messages,
    setMessages,
    input,
    setInput,
    sending,
    applying,
    pendingOps,
    setPendingOps,
    editingOpsJson,
    setEditingOpsJson,
    highlightIds,
    // Refs
    scrollRef,
    inputRef,
    // Handler
    handleSend,
    handleKeyDown,
    handleConfirmOps,
    handleCancelOps,
    handleCloseItem,
    handleUpdateItem,
    loadState,
    endSession,
  };
}
